import { useEffect, useState, type ComponentType } from "react";
export type { DataTab } from "./dataTab.ts";
import type { DataTab } from "./dataTab.ts";
import { readCurves, writeCurves } from "../model/inp/curves.ts";
import { readTimeSeries, writeTimeSeries } from "../model/inp/timeseries.ts";
import { readPatterns, writePatterns } from "../model/inp/patterns.ts";
import { readPollutants, writePollutants } from "../model/inp/pollutants.ts";
import { readLandUses, writeLandUses } from "../model/inp/landuses.ts";
import {
  readBuildup,
  readWashoff,
  writeBuildup,
  writeWashoff,
} from "../model/inp/buildupWashoff.ts";
import { readLidControls, writeLidControls } from "../model/inp/lidControls.ts";
import { readAquifers, writeAquifers } from "../model/inp/aquifers.ts";
import { readSnowPacks, writeSnowPacks } from "../model/inp/snowpacks.ts";
import {
  readAdjustments,
  readEvaporation,
  readTemperature,
  writeAdjustments,
  writeEvaporation,
  writeTemperature,
  type Adjustments,
  type Evaporation,
  type Temperature,
} from "../model/inp/climatology.ts";
import { readTransects, writeTransects } from "../model/inp/transects.ts";
import { readUnitHydrographs, writeUnitHydrographs } from "../model/inp/rdii.ts";
import {
  readControlExpressions,
  readControlRules,
  readControlVariables,
  writeControlRules,
} from "../model/inp/controlRules.ts";
import { parseControlVariables } from "../engine/controlRules/controlRuleParser.ts";
import { readStreets, writeStreets } from "../model/inp/streets.ts";
import { readInletDesigns, writeInletDesigns } from "../model/inp/inlets.ts";
import { useT, type Translate } from "../i18n/hooks.ts";
import type { TextKey } from "../i18n/en.ts";
import { Dialog } from "./Dialog.tsx";
import { useIsDirty } from "./useIsDirty.ts";
import { DialogTitleActionProvider, type DialogTitleAction } from "./useDialogTitleAction.ts";
import { makeSimpleTabPanel, type TabPanelProps } from "./makeSimpleTabPanel.tsx";
import { TabDialogActions } from "./TabDialogActions.tsx";
import { DATA_TAB_ICONS } from "./dataTabIcons.tsx";
import { PlusIcon } from "./PlusIcon.tsx";
import { CurvesEditor } from "./CurvesEditor.tsx";
import { TimeSeriesEditor } from "./TimeSeriesEditor.tsx";
import { PatternsEditor } from "./PatternsEditor.tsx";
import { PollutantsEditor } from "./PollutantsEditor.tsx";
import { LandUsesEditor } from "./LandUsesEditor.tsx";
import { LidControlsEditor } from "./LidControlsEditor.tsx";
import { AquifersEditor } from "./AquifersEditor.tsx";
import { SnowPacksEditor } from "./SnowPacksEditor.tsx";
import { ClimatologyEditor } from "./ClimatologyEditor.tsx";
import { TransectsEditor } from "./TransectsEditor.tsx";
import { UnitHydrographsEditor } from "./UnitHydrographsEditor.tsx";
import { ControlRulesEditor } from "./ControlRulesEditor.tsx";
import type { ElementNames } from "./controlRuleDraft.ts";
import { StreetsEditor } from "./StreetsEditor.tsx";
import { InletsEditor } from "./InletsEditor.tsx";

// A handful of tab panels below hold a setter-less `useState(() => readX(rawSections))`
// (`pollutants` in LandUsesTabPanel, `timeSeries`/`patterns` in
// ClimatologyTabPanel, `namedVariables`/`expressionNames`/`curveNames`/
// `timeSeriesNames` in ControlRulesTabPanel) — deliberately, not a leftover
// of a setter that got dropped. Each is a READ-ONLY reference list (curve/
// timeseries/pollutant names, control-rule variable names) another
// section's own editor here needs for its dropdowns, never edited by ITS
// panel. `useState`'s own lazy initializer is what's wanted, not a plain
// `const` (would re-run the read on every render) or `useMemo` keyed on
// `rawSections` (would re-derive it if some OTHER tab's edit changes that
// prop while this one stays open) — a frozen-at-mount snapshot, computed
// once and intentionally never updated for the rest of this dialog's
// lifetime, same as reopening it (a fresh mount) already does implicitly.
function tabTitle(t: Translate, tab: DataTab): string {
  const keys: Record<DataTab, TextKey> = {
    curves: "projectData.tabCurves",
    timeseries: "projectData.tabTimeSeries",
    patterns: "projectData.tabPatterns",
    pollutants: "projectData.tabPollutants",
    landuses: "projectData.tabLandUses",
    lidcontrols: "projectData.tabLidControls",
    aquifers: "projectData.tabAquifers",
    snowpacks: "projectData.tabSnowPacks",
    climatology: "projectData.tabClimatology",
    transects: "projectData.tabTransects",
    hydrographs: "projectData.tabHydrographs",
    controlrules: "projectData.tabControlRules",
    streets: "projectData.tabStreets",
    inlets: "projectData.tabInlets",
  };
  return t(keys[tab]);
}

const CurvesTabPanel = makeSimpleTabPanel({
  read: readCurves,
  write: writeCurves,
  Editor: CurvesEditor,
  editorProps: (curves, setCurves, selectedName, onSelect) => ({
    curves,
    onChange: setCurves,
    selectedName,
    onSelect,
  }),
});

const TimeSeriesTabPanel = makeSimpleTabPanel({
  read: readTimeSeries,
  write: writeTimeSeries,
  Editor: TimeSeriesEditor,
  editorProps: (series, setSeries, selectedName, onSelect) => ({
    series,
    onChange: setSeries,
    selectedName,
    onSelect,
  }),
});

const PatternsTabPanel = makeSimpleTabPanel({
  read: readPatterns,
  write: writePatterns,
  Editor: PatternsEditor,
  editorProps: (patterns, setPatterns, selectedName, onSelect) => ({
    patterns,
    onChange: setPatterns,
    selectedName,
    onSelect,
  }),
});

const PollutantsTabPanel = makeSimpleTabPanel({
  read: readPollutants,
  write: writePollutants,
  Editor: PollutantsEditor,
  editorProps: (pollutants, setPollutants, selectedName, onSelect) => ({
    pollutants,
    onChange: setPollutants,
    selectedName,
    onSelect,
  }),
});

/** `pollutants`: a read-only reference list for the Removals sub-table. */
const LidControlsTabPanel = makeSimpleTabPanel({
  read: readLidControls,
  write: writeLidControls,
  readExtra: readPollutants,
  Editor: LidControlsEditor,
  editorProps: (controls, setControls, selectedName, onSelect, pollutants) => ({
    controls,
    onChange: setControls,
    selectedName,
    onSelect,
    pollutants,
  }),
});

/** `patterns`: a read-only reference list for the evaporation-pattern picker. */
const AquifersTabPanel = makeSimpleTabPanel({
  read: readAquifers,
  write: writeAquifers,
  readExtra: readPatterns,
  Editor: AquifersEditor,
  editorProps: (aquifers, setAquifers, selectedName, onSelect, patterns) => ({
    aquifers,
    onChange: setAquifers,
    selectedName,
    onSelect,
    patterns,
  }),
});

const SnowPacksTabPanel = makeSimpleTabPanel({
  read: readSnowPacks,
  write: writeSnowPacks,
  Editor: SnowPacksEditor,
  editorProps: (packs, setPacks, selectedName, onSelect) => ({
    packs,
    onChange: setPacks,
    selectedName,
    onSelect,
  }),
});

const TransectsTabPanel = makeSimpleTabPanel({
  read: readTransects,
  write: writeTransects,
  Editor: TransectsEditor,
  editorProps: (transects, setTransects, selectedName, onSelect) => ({
    transects,
    onChange: setTransects,
    selectedName,
    onSelect,
  }),
});

function raingageNamesFromSections(rawSections: ReadonlyMap<string, string[][]>): string[] {
  return (rawSections.get("RAINGAGES") ?? [])
    .map((row) => row[0])
    .filter((name): name is string => Boolean(name));
}

/** `raingageNames` reads directly off [RAINGAGES] — no typed `readRaingages` helper exists elsewhere for this. */
const HydrographsTabPanel = makeSimpleTabPanel({
  read: readUnitHydrographs,
  write: writeUnitHydrographs,
  readExtra: raingageNamesFromSections,
  Editor: UnitHydrographsEditor,
  editorProps: (hydrographs, setHydrographs, selectedName, onSelect, raingageNames) => ({
    hydrographs,
    onChange: setHydrographs,
    selectedName,
    onSelect,
    raingageNames,
  }),
});

const StreetsTabPanel = makeSimpleTabPanel({
  read: readStreets,
  write: writeStreets,
  Editor: StreetsEditor,
  editorProps: (streets, setStreets, selectedName, onSelect) => ({
    streets,
    onChange: setStreets,
    selectedName,
    onSelect,
  }),
});

const InletsTabPanel = makeSimpleTabPanel({
  read: readInletDesigns,
  write: writeInletDesigns,
  Editor: InletsEditor,
  editorProps: (inlets, setInlets, selectedName, onSelect) => ({
    inlets,
    onChange: setInlets,
    selectedName,
    onSelect,
  }),
});

/** `pollutants` is a read-only reference list for the buildup/washoff sub-tables — edited only in its own separate tab. */
function LandUsesTabPanel({ rawSections, onSave, onClose, onDirtyChange }: TabPanelProps) {
  const [landUses, setLandUses] = useState(() => readLandUses(rawSections));
  const [selected, setSelected] = useState<string | null>(() => landUses[0]?.name ?? null);
  const [buildup, setBuildup] = useState(() => readBuildup(rawSections));
  const [washoff, setWashoff] = useState(() => readWashoff(rawSections));
  const [pollutants] = useState(() => readPollutants(rawSections));
  const dirty = useIsDirty([landUses, buildup, washoff]);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  return (
    <>
      <LandUsesEditor
        landUses={landUses}
        onChange={setLandUses}
        selectedName={selected}
        onSelect={setSelected}
        pollutants={pollutants}
        buildup={buildup}
        onChangeBuildup={setBuildup}
        washoff={washoff}
        onChangeWashoff={setWashoff}
      />
      <TabDialogActions
        onSave={() =>
          onSave(writeWashoff(writeBuildup(writeLandUses(rawSections, landUses), buildup), washoff))
        }
        onClose={onClose}
      />
    </>
  );
}

/** No name/selection state, unlike every other panel here — [EVAPORATION]/[TEMPERATURE]/[ADJUSTMENTS] are each one project-wide setup, not a named list. */
function ClimatologyTabPanel({ rawSections, onSave, onClose, onDirtyChange }: TabPanelProps) {
  const [evaporation, setEvaporation] = useState<Evaporation>(() => readEvaporation(rawSections));
  const [temperature, setTemperature] = useState<Temperature>(() => readTemperature(rawSections));
  const [adjustments, setAdjustments] = useState<Adjustments>(() => readAdjustments(rawSections));
  const [timeSeries] = useState(() => readTimeSeries(rawSections));
  const [patterns] = useState(() => readPatterns(rawSections));
  const dirty = useIsDirty([evaporation, temperature, adjustments]);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  return (
    <>
      <ClimatologyEditor
        evaporation={evaporation}
        onChangeEvaporation={setEvaporation}
        temperature={temperature}
        onChangeTemperature={setTemperature}
        adjustments={adjustments}
        onChangeAdjustments={setAdjustments}
        timeSeries={timeSeries}
        patterns={patterns}
      />
      <TabDialogActions
        onSave={() =>
          onSave(
            writeAdjustments(
              writeTemperature(writeEvaporation(rawSections, evaporation), temperature),
              adjustments
            )
          )
        }
        onClose={onClose}
      />
    </>
  );
}

/**
 * A section's row names, first column, merged across every section a rule's
 * object keyword can draw an id from. Deduplicated: most sections have
 * exactly one row per name, but [CURVES]/[TIMESERIES] repeat the same name
 * on every continuation row (one row per data point) — without dedup, the
 * curve/timeseries id dropdowns would show one duplicate `<option>` per point.
 */
function namesFromSections(
  rawSections: ReadonlyMap<string, string[][]>,
  sections: string[]
): string[] {
  const names = sections.flatMap((section) =>
    (rawSections.get(section) ?? [])
      .map((row) => row[0])
      .filter((name): name is string => Boolean(name))
  );
  return [...new Set(names)];
}

function ControlRulesTabPanel({ rawSections, onSave, onClose, onDirtyChange }: TabPanelProps) {
  const [rules, setRules] = useState(() => readControlRules(rawSections));
  const [selected, setSelected] = useState<string | null>(() => rules[0]?.name ?? null);
  const dirty = useIsDirty(rules);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  const [namedVariables] = useState(() => parseControlVariables(readControlVariables(rawSections)));
  const [expressionNames] = useState(
    () => new Set(readControlExpressions(rawSections).map((d) => d.name))
  );
  // One combined list for JUNCTIONS/OUTFALLS/STORAGE/DIVIDERS, since a
  // rule's NODE keyword is generic across all 4. Each link/structure kind
  // gets its own separate list instead — a merged one is a real correctness
  // trap: found live, it let "PUMP" silently default its id to a CONDUIT's name.
  const [names] = useState<ElementNames>(() => ({
    node: namesFromSections(rawSections, ["JUNCTIONS", "OUTFALLS", "STORAGE", "DIVIDERS"]),
    conduit: namesFromSections(rawSections, ["CONDUITS"]),
    pump: namesFromSections(rawSections, ["PUMPS"]),
    orifice: namesFromSections(rawSections, ["ORIFICES"]),
    weir: namesFromSections(rawSections, ["WEIRS"]),
    outlet: namesFromSections(rawSections, ["OUTLETS"]),
    gage: namesFromSections(rawSections, ["RAINGAGES"]),
  }));
  const [curveNames] = useState(() => namesFromSections(rawSections, ["CURVES"]));
  const [timeSeriesNames] = useState(() => namesFromSections(rawSections, ["TIMESERIES"]));
  return (
    <>
      <ControlRulesEditor
        rules={rules}
        onChange={setRules}
        selectedName={selected}
        onSelect={setSelected}
        namedVariables={namedVariables}
        expressionNames={expressionNames}
        names={names}
        curveNames={curveNames}
        timeSeriesNames={timeSeriesNames}
      />
      <TabDialogActions
        onSave={() => onSave(writeControlRules(rawSections, rules))}
        onClose={onClose}
      />
    </>
  );
}

/**
 * One panel component per `DataTab`. Dispatching through a
 * `Record<DataTab, Component>` means React mounts (and runs the hooks of)
 * only the active tab's panel — calling every tab's own `useState`/
 * `readXxx(rawSections)` unconditionally (the rules of hooks would otherwise
 * require it, since `onlyTab` never changes after mount) would parse every
 * section on every open even though only one tab's editor ever renders.
 */
const TAB_PANELS: Record<DataTab, ComponentType<TabPanelProps>> = {
  curves: CurvesTabPanel,
  timeseries: TimeSeriesTabPanel,
  patterns: PatternsTabPanel,
  pollutants: PollutantsTabPanel,
  landuses: LandUsesTabPanel,
  lidcontrols: LidControlsTabPanel,
  aquifers: AquifersTabPanel,
  snowpacks: SnowPacksTabPanel,
  climatology: ClimatologyTabPanel,
  transects: TransectsTabPanel,
  hydrographs: HydrographsTabPanel,
  controlrules: ControlRulesTabPanel,
  streets: StreetsTabPanel,
  inlets: InletsTabPanel,
};

/**
 * The non-map-object data every real SWMM project can carry — curves, time
 * series, patterns, pollutants, etc. — opened from `ProjectDataMenu.tsx`'s
 * toolbar dropdown, one item per `DataTab`.
 *
 * `onlyTab` narrows this dialog to exactly one section, with no tab bar at
 * all: an earlier version showed all sections behind an internal tab bar in
 * one shared dialog, which read as bad UX here specifically — unlike
 * OptionsDialog.tsx's sections (fixed there with real tabs instead of
 * everything stacked), here tab-switching moved out of the dialog entirely,
 * into a toolbar dropdown that opens a fresh, single-purpose dialog per pick.
 */
export function ProjectDataDialog({
  rawSections,
  onSave,
  onClose,
  onlyTab,
}: {
  rawSections: ReadonlyMap<string, string[][]>;
  onSave: (raw: ReadonlyMap<string, string[][]>) => void;
  onClose: () => void;
  onlyTab: DataTab;
}) {
  const t = useT();
  const Panel = TAB_PANELS[onlyTab];
  const [dirty, setDirty] = useState(false);
  const [addAction, setAddAction] = useState<DialogTitleAction | null>(null);
  return (
    <Dialog
      title={tabTitle(t, onlyTab)}
      titleIcon={DATA_TAB_ICONS[onlyTab]}
      titleAction={
        addAction && (
          <button
            className="dialog-title-add"
            onClick={addAction.onAdd}
            title={addAction.label}
            aria-label={addAction.label}
          >
            <PlusIcon />
          </button>
        )
      }
      onClose={onClose}
      wide
      isDirty={dirty}
    >
      <DialogTitleActionProvider value={setAddAction}>
        <Panel
          rawSections={rawSections}
          onSave={onSave}
          onClose={onClose}
          onDirtyChange={setDirty}
        />
      </DialogTitleActionProvider>
    </Dialog>
  );
}
